/*
 * بسته‌بندی زیپ در مرورگر — بدون هیچ وابستگی، و بدون فشرده‌سازی.
 *
 * چرا بدون فشرده‌سازی: خروجی دستگاه از قبل فشرده است. اندازه‌گیری روی فایل
 * واقعی نشان داد Transfer Syntax برابر 1.2.840.10008.1.2.4.70 است، یعنی
 * JPEG Lossless. دوباره فشرده کردنش فقط CPU می‌سوزاند و حجم را کم نمی‌کند.
 * پس روش «store» — زیپ اینجا فقط ظرف است، نه فشرده‌ساز.
 *
 * چرا کتابخانه نمی‌آوریم: فرانت‌اند این پروژه عمداً چهار وابستگی دارد، و
 * زیپِ store در صد خط جا می‌شود.
 *
 * حافظه: بایت‌های فایل هیچ‌وقت یک‌جا در رم نمی‌نشینند. تکه‌ها به‌صورت آرایه‌ای
 * از Blob و هدر جمع می‌شوند و مرورگر خودش پشت صحنه روی دیسک نگهشان می‌دارد.
 */

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;

// بیت ۱۱ به مرورگر و به ابزارهای دیگر می‌گوید نام فایل UTF-8 است.
const UTF8_FLAG = 0x0800;

// فرمت پایه‌ی زیپ تا ۴ گیگابایت می‌رود؛ فراتر از آن zip64 لازم است که
// اینجا پیاده نشده. سقف سرور کمتر از این است، ولی صریح خطا بدهیم بهتر از
// ساختن آرشیو خرابِ بی‌صدا است.
const MAX_TOTAL = 4 * 1024 * 1024 * 1024 - 1;

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

function crc32(bytes, seed = 0) {
  let crc = ~seed;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return ~crc >>> 0;
}

/** تاریخ و ساعت به قالب MS-DOS که زیپ می‌خواهد. */
function dosDateTime(date) {
  const time =
    (Math.floor(date.getSeconds() / 2) & 0x1f) |
    ((date.getMinutes() & 0x3f) << 5) |
    ((date.getHours() & 0x1f) << 11);
  const day =
    (date.getDate() & 0x1f) |
    (((date.getMonth() + 1) & 0x0f) << 5) |
    ((Math.max(0, date.getFullYear() - 1980) & 0x7f) << 9);
  return { time, day };
}

function writer(size) {
  const buffer = new ArrayBuffer(size);
  const view = new DataView(buffer);
  let offset = 0;
  return {
    u16(value) { view.setUint16(offset, value, true); offset += 2; },
    u32(value) { view.setUint32(offset, value >>> 0, true); offset += 4; },
    bytes(source) { new Uint8Array(buffer, offset, source.length).set(source); offset += source.length; },
    done() { return new Uint8Array(buffer); },
  };
}

/**
 * CRC و اندازه‌ی یک فایل را تکه‌تکه حساب می‌کند.
 *
 * فایل بزرگ را یک‌جا نمی‌خوانیم: تکه‌ی هشت‌مگابایتی کافی است و رم را
 * درگیر نمی‌کند.
 */
async function checksum(file, onChunk) {
  const CHUNK = 8 * 1024 * 1024;
  let crc = 0;
  for (let start = 0; start < file.size; start += CHUNK) {
    const slice = file.slice(start, Math.min(start + CHUNK, file.size));
    // eslint-disable-next-line no-await-in-loop
    const bytes = new Uint8Array(await slice.arrayBuffer());
    crc = crc32(bytes, crc);
    if (onChunk) onChunk(bytes.length);
  }
  return crc;
}

/**
 * فهرستی از {path, file} را به یک Blob زیپ تبدیل می‌کند.
 *
 * `onProgress(processedBytes, totalBytes)` برای نوار پیشرفت صدا زده می‌شود.
 */
export async function buildZip(entries, onProgress) {
  const total = entries.reduce((sum, entry) => sum + entry.file.size, 0);
  if (total > MAX_TOTAL) {
    throw new Error('مجموع حجم فایل‌ها از ۴ گیگابایت بیشتر است.');
  }

  const encoder = new TextEncoder();
  const parts = [];
  const central = [];
  let offset = 0;
  let processed = 0;

  // یک‌بار ساخته می‌شود، نه داخل حلقه.
  const report = (chunkSize) => {
    processed += chunkSize;
    if (onProgress) onProgress(processed, total);
  };

  for (const entry of entries) {
    const name = encoder.encode(entry.path);
    const { file } = entry;
    // eslint-disable-next-line no-await-in-loop
    const crc = await checksum(file, report);
    const { time, day } = dosDateTime(new Date(file.lastModified || Date.now()));

    const header = writer(30 + name.length);
    header.u32(LOCAL_SIG);
    header.u16(20);            // نسخه‌ی لازم برای باز کردن
    header.u16(UTF8_FLAG);
    header.u16(0);             // روش: store
    header.u16(time);
    header.u16(day);
    header.u32(crc);
    header.u32(file.size);     // حجم فشرده = حجم اصلی
    header.u32(file.size);
    header.u16(name.length);
    header.u16(0);             // extra
    header.bytes(name);

    parts.push(header.done(), file);

    const record = writer(46 + name.length);
    record.u32(CENTRAL_SIG);
    record.u16(20);            // نسخه‌ی سازنده
    record.u16(20);
    record.u16(UTF8_FLAG);
    record.u16(0);
    record.u16(time);
    record.u16(day);
    record.u32(crc);
    record.u32(file.size);
    record.u32(file.size);
    record.u16(name.length);
    record.u16(0);             // extra
    record.u16(0);             // comment
    record.u16(0);             // شماره‌ی دیسک
    record.u16(0);             // صفات داخلی
    record.u32(0);             // صفات خارجی
    record.u32(offset);        // جای هدر محلی
    record.bytes(name);
    central.push(record.done());

    offset += 30 + name.length + file.size;
  }

  const centralSize = central.reduce((sum, block) => sum + block.length, 0);
  const eocd = writer(22);
  eocd.u32(EOCD_SIG);
  eocd.u16(0);
  eocd.u16(0);
  eocd.u16(entries.length);
  eocd.u16(entries.length);
  eocd.u32(centralSize);
  eocd.u32(offset);
  eocd.u16(0);                 // بدون توضیح

  return new Blob([...parts, ...central, eocd.done()], { type: 'application/zip' });
}

// پوشه‌هایی که هرگز آپلود نمی‌شوند — باید با api/dicom.py هم‌خوان بماند.
//
// `Cache` کشِ نرم‌افزار دستگاه است: روی یک مطالعه‌ی واقعی ۴۰ مگابایت،
// یعنی ۱۲٪ حجم آپلود، بدون هیچ ارزش تشخیصی.
export const EXCLUDED_DIRS = ['cache'];

function isExcluded(path) {
  return path
    .split('/')
    .slice(0, -1)
    .some((part) => EXCLUDED_DIRS.includes(part.trim().toLowerCase()));
}

/** پیمایش بازگشتی یک پوشه‌ی رهاشده در مرورگر. */
async function walkEntry(entry, prefix, out) {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    out.push({ path: prefix + entry.name, file });
    return;
  }
  const reader = entry.createReader();
  // readEntries هر بار حداکثر ۱۰۰ مورد می‌دهد؛ تا خالی شدن باید تکرار شود.
  // بدون این حلقه، پوشه‌ی ۷۰۰ فایلی بی‌صدا ناقص خوانده می‌شود.
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await new Promise((resolve, reject) =>
      reader.readEntries(resolve, reject)
    );
    if (!batch.length) break;
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.map((child) => walkEntry(child, `${prefix}${entry.name}/`, out)));
  }
}

/**
 * فایل‌های یک عملیات درگ‌اند‌دراپ را جمع می‌کند — چه پوشه باشد چه فایل.
 *
 * پوشه‌های کش کنار گذاشته می‌شوند.
 */
export async function collectDropped(dataTransfer) {
  const out = [];
  const entries = Array.from(dataTransfer.items)
    .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
    .filter(Boolean);

  if (entries.length) {
    await Promise.all(entries.map((entry) => walkEntry(entry, '', out)));
  } else {
    // مرورگری که getAsEntry ندارد: فقط فایل‌های تخت.
    Array.from(dataTransfer.files).forEach((file) => out.push({ path: file.name, file }));
  }

  return out.filter((item) => !isExcluded(item.path));
}

/** فایل‌های انتخاب‌شده از `<input webkitdirectory>` یا انتخاب چندتایی. */
export function collectPicked(fileList) {
  return Array.from(fileList)
    .map((file) => ({ path: file.webkitRelativePath || file.name, file }))
    .filter((item) => !isExcluded(item.path));
}
